import React, { useEffect, useState } from 'react';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { approve } from 'src/utils/contracts/resolver';
import { handleError } from 'src/utils/errors/common';
import { isValidAddress } from 'src/utils/common';
import { Chain } from 'src/types/enums';

const Manager: React.FC<{
  domain: Domain;
  selectDomain: (domain: Domain | undefined) => void;
}> = ({ domain, selectDomain }) => {
  const [manager, setManager] = useState<string>('');
  const [managers, setManagers] = useState<string[]>(domain.managers);

  const handleUpdate = async (manager: string, isApproved: boolean) => {
    if (domain.resolver?.resolverAddress) {
      try {
        await approve(
          domain.name.split('.'),
          manager,
          isApproved,
          domain.resolver?.resolverAddress,
        );

        const updatedDomain = { ...domain };

        if (isApproved) {
          updatedDomain.managers = [...updatedDomain.managers, manager];
          selectDomain(updatedDomain);
        } else {
          updatedDomain.managers = updatedDomain.managers.filter(
            m => m !== manager,
          );
          selectDomain(updatedDomain);
        }

        setManagers(updatedDomain.managers);

        setManager('');
      } catch (error) {
        handleError(error);
      }
    }
  };

  useEffect(() => {
    console.log('useEffect');
    setManagers(domain.managers);
  }, [domain]);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sx={{ marginTop: 4 }}>
        <Typography variant="h6" gutterBottom>
          Managers
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="managers table">
            <TableBody>
              {managers.map((manager, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    {manager.slice(0, 6)} ... {manager.slice(-4)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleUpdate(manager, false)}
                    >
                      <Delete sx={{ color: 'red' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={8}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter manager address"
          value={manager}
          onChange={e => setManager(e.target.value)}
          disabled={domain.resolver?.resolverAddress.length === 0}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '46px',
              '&:hover fieldset': {
                borderColor: 'grey.600',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'grey.600',
              },
            },
          }}
          title={
            !isValidAddress(manager, Chain.C)
              ? 'Must be a valid C-Chain address'
              : ''
          }
        />
      </Grid>
      <Grid item xs={4}>
        <Button
          variant="contained"
          fullWidth
          sx={{
            color: 'text.primary',
            backgroundColor: 'primary.main',
            padding: '10px 16px',
          }}
          onClick={() => handleUpdate(manager, true)}
          disabled={
            domain.resolver?.resolverAddress.length === 0 ||
            !isValidAddress(manager, Chain.C)
          }
        >
          Add
        </Button>
      </Grid>
    </Grid>
  );
};

export default React.memo(Manager);
